import { useEffect, useRef, useState } from "react";
import { Ruler, X, Copy } from "lucide-react";

type Issue = {
  type: "overflow" | "tap-target" | "truncated";
  selector: string;
  detail: string;
};

const HIGHLIGHT_ATTR = "data-layout-issue";

function getSelector(el: Element): string {
  if (el.id) return `#${el.id}`;
  const cls = (el.getAttribute("class") || "")
    .split(/\s+/)
    .filter((c) => c && !c.startsWith("hover:") && !c.startsWith("focus:"))
    .slice(0, 2)
    .join(".");
  return `${el.tagName.toLowerCase()}${cls ? "." + cls : ""}`;
}

export default function LayoutInspector() {
  const [open, setOpen] = useState(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [vw, setVw] = useState(window.innerWidth);
  const debounceRef = useRef<number | null>(null);

  const breakpoint =
    vw < 640 ? "xs (mobile)" : vw < 768 ? "sm" : vw < 1024 ? "md (tablet)" : vw < 1280 ? "lg" : "xl (desktop)";

  const scan = () => {
    const found: Issue[] = [];
    const all = document.body.querySelectorAll<HTMLElement>("*");
    document.querySelectorAll(`[${HIGHLIGHT_ATTR}]`).forEach((el) => {
      (el as HTMLElement).style.outline = "";
      el.removeAttribute(HIGHLIGHT_ATTR);
    });
    const isMobile = window.innerWidth < 768;

    all.forEach((el) => {
      if (el.closest("[data-dev-panel]")) return;
      // Overflow
      if (el.scrollWidth > el.clientWidth + 1 && el.clientWidth > 0) {
        const style = getComputedStyle(el);
        if (style.overflowX === "visible" || style.overflowX === "clip") {
          found.push({
            type: "overflow",
            selector: getSelector(el),
            detail: `scrollW ${el.scrollWidth} > clientW ${el.clientWidth}`,
          });
          el.style.outline = "2px solid hsl(0 80% 60%)";
          el.setAttribute(HIGHLIGHT_ATTR, "overflow");
        }
      }
      // Tap target on mobile
      if (isMobile && (el.tagName === "BUTTON" || el.tagName === "A")) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0 && (r.width < 44 || r.height < 44)) {
          found.push({
            type: "tap-target",
            selector: getSelector(el),
            detail: `${Math.round(r.width)}×${Math.round(r.height)}px`,
          });
          el.style.outline = "2px dashed hsl(30 90% 55%)";
          el.setAttribute(HIGHLIGHT_ATTR, "tap");
        }
      }
      // Truncated text
      if (el.className && typeof el.className === "string" && el.className.includes("truncate")) {
        if (el.scrollWidth > el.offsetWidth + 1) {
          found.push({
            type: "truncated",
            selector: getSelector(el),
            detail: `"${(el.textContent || "").trim().slice(0, 40)}…"`,
          });
        }
      }
    });

    setIssues(found.slice(0, 200));
  };

  const queueScan = () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(scan, 500);
  };

  useEffect(() => {
    if (!open) {
      document.querySelectorAll(`[${HIGHLIGHT_ATTR}]`).forEach((el) => {
        (el as HTMLElement).style.outline = "";
        el.removeAttribute(HIGHLIGHT_ATTR);
      });
      return;
    }
    scan();
    const onResize = () => {
      setVw(window.innerWidth);
      queueScan();
    };
    window.addEventListener("resize", onResize);
    const mo = new MutationObserver(queueScan);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    return () => {
      window.removeEventListener("resize", onResize);
      mo.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copyReport = () => {
    const report = {
      url: window.location.pathname,
      viewport: { width: vw, breakpoint },
      issues,
    };
    navigator.clipboard.writeText(JSON.stringify(report, null, 2));
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Layout Inspector"
        className="flex items-center gap-2 rounded-full bg-foreground text-background px-4 py-2 text-sm font-semibold shadow-lg hover:opacity-90 transition"
      >
        <Ruler className="h-4 w-4" />
        Inspect
      </button>
    );
  }

  const counts = {
    overflow: issues.filter((i) => i.type === "overflow").length,
    tap: issues.filter((i) => i.type === "tap-target").length,
    trunc: issues.filter((i) => i.type === "truncated").length,
  };

  return (
    <div
      data-dev-panel
      className="w-[360px] max-h-[70vh] flex flex-col rounded-xl bg-background border border-border shadow-2xl overflow-hidden"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Ruler className="h-4 w-4" />
          Layout Inspector
        </div>
        <div className="flex items-center gap-1">
          <button onClick={copyReport} className="p-1.5 hover:bg-muted rounded" title="Copy JSON report">
            <Copy className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-muted rounded">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border flex justify-between">
        <span>{vw}px · {breakpoint}</span>
        <span>
          <span className="text-red-500">{counts.overflow} overflow</span> ·{" "}
          <span className="text-orange-500">{counts.tap} tap</span> ·{" "}
          <span className="text-yellow-500">{counts.trunc} trunc</span>
        </span>
      </div>
      <div className="flex-1 overflow-auto text-xs font-mono p-2 space-y-1">
        {issues.length === 0 && <div className="text-muted-foreground p-2">No issues found.</div>}
        {issues.map((i, idx) => (
          <div key={idx} className="px-2 py-1 rounded hover:bg-muted/50">
            <span
              className={
                i.type === "overflow"
                  ? "text-red-500"
                  : i.type === "tap-target"
                  ? "text-orange-500"
                  : "text-yellow-500"
              }
            >
              [{i.type}]
            </span>{" "}
            <span className="text-foreground">{i.selector}</span>
            <div className="text-muted-foreground pl-4">{i.detail}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
